import { faAt, faCircle, faClose, faHistory, faPaperPlane, faPenToSquare, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { callLLMChat, callLLMDeleteConversation, callLLMGetConversations, callLLMGetConversationMessages, callLLMPing } from '../../amplify/api/llm-chat';
import { getWSService } from '../../amplify/api/llm-web-socket';
import { callSearchPatients } from '../../amplify/appsync/search-patients';
import { useGlobalOptions } from '../common/GlobalOptionsContext';
import './AssistantPanel.scss';
import CopyToClipboardButton from './CopyToClipboardButton';
import FeedbackModal from './FeedbackModal';
import { MarkdownRenderer } from './MarkdownRenderer';
import ToolMetadataBox from './ToolMetadataBox';
import UpdateDashboardButton from './UpdateDashboardButton';
import { truncateString } from '../dashboard/llm/utils';
import { trackEvent } from '../../amplify/appsync/user-events';
import { Citation, MentionData, Message } from './assistant-models';
import { jsonrepair } from 'jsonrepair';
import { ScanMulti } from '../dashboard/llm/common/models';
import { generateCitationMapping } from './assistant-utils';
import { readBinaryFile } from '../../amplify/s3/storage';
import { getUserFriendlyDateStringWithoutSecond } from '../../utils/time';

interface AssistantPanelProps {
    isOpen: boolean;
    setIsOpen: (state: boolean) => void;
    ws: any,
    user: any,
    tenant: any,
    width: number,
    setWidth: (w: number) => void,
}

// Custom hook for debounced values

const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

const mockCitations = [
    {
        id: "1",
        title: "The Impact of AI on Modern Healthcare",
        author: "Dr. Sarah Johnson",
        date: "2024-03-15",
        description: "A comprehensive study on artificial intelligence applications in healthcare systems.",
        url: "https://example.com/ai-healthcare-study"
    },
    {
        id: "2", 
        title: "Machine Learning Best Practices",
        author: "Tech Research Institute",
        date: "2024-02-10",
        description: "Guidelines for implementing machine learning in enterprise environments.",
        url: "s3://my-bucket/documents/ml-best-practices.pdf"
    },
    {
        id: "3",
        title: "Future of Autonomous Systems",
        author: "Prof. Michael Chen",
        date: "2024-01-20",
        description: "Exploring the ethical implications of autonomous decision-making systems."
        // Note: no URL provided, will show basic popover
    }
];

const mockMessages: Message[] = [
    { message_id: '1', role: 'user', text: "How many pain meds was this guy on?" },
    {
        message_id: '2', role: 'tool', metadata: {
            name: "retrieve_health_records",
            source: "Google",
            query: "Latest AI news",
            timestamp: "2025-06-20T12:00:00Z"
        }
    },{
        message_id: '3', role: 'tool', metadata: {
            name: "update_memory",
            source: "Google",
            query: "Latest AI news",
            timestamp: "2025-06-20T12:00:00Z"
        }
    },
    { message_id: '4', role: 'assistant', text: "This guy was on one pain med \n- [Pediatric ED Stroke/ICP Pathways](s3://physiohub-ed-kb/original-docs/Clinical%20Pathways/Neurology/Ped%20ED%20presentation%20-%20Stroke.pptx) " },
    { message_id: '5', role: 'assistant', text: "hello \n\n | Name     | Age | City        |\n|----------|-----|-------------|\n| Alice    | 30  | New York    |\n| Bob      | 25  | San Francisco |\n| Charlie  | 35  | Toronto     |" },
    { message_id: '5', role: 'assistant', text: "hello - For more information, \n\n go see the detailed analysis [1] and implementation guide [2].", citations: mockCitations },
    
];

let socketConnection: any;
let sessionId: string;

const MIN_WIDTH = 300;
const MAX_WIDTH = 800;

const AssistantPanel: React.FC<AssistantPanelProps> = ({ isOpen, setIsOpen, ws, user, tenant, width, setWidth }) => {
    const { getPageContext } = useGlobalOptions();

    const scrollCheckpointRef = useRef<HTMLDivElement>(null);

    const [debug, setDebug] = useState<any>(false);

    const [isWebSocketConnecting, setIsWebSocketConnecting] = useState<any>(false);

    const [showMentions, setShowMentions] = useState<boolean>(false);
    const [mentions, setMentions] = useState<MentionData[]>([]);
    const [mentionQuery, setMentionQuery] = useState<string>("");
    // const [messages, setMessages] = useState<Message[]>(mockMessages);
    const [messages, setMessages] = useState<Message[]>([]);
    // The message that is being built through the streaming response
    const [temporaryMessage, setTemporaryMessage] = useState<Message>();
    // In-line citation source data for health records
    const [scanMulti, setScanMulti] = useState<ScanMulti>();
    const [isLoading, setIsLoading] = useState<any>(false);
    const [isTextDisabled, setIsTextDisabled] = useState<any>(false);

    const editorRef = useRef<HTMLDivElement>(null);
    const mentionPanelRef = useRef<HTMLDivElement>(null);

    const [users, setUsers] = useState<MentionData[]>([]);
    const [cursorPosition, setCursorPosition] = useState<number>(0);
    const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
    const [userSearchError, setUserSearchError] = useState<string>("");
    const [messageIdToFeedback, setMessageIdToFeedback] = useState<any>({});

    const abortControllerRef = useRef<AbortController | null>(null);

    const [isResizing, setIsResizing] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);

    // Conversation history state
    const [showHistory, setShowHistory] = useState(false);
    const [conversations, setConversations] = useState<any[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);

    // Debounce the mention query
    const debouncedMentionQuery = useDebounce(mentionQuery, 300);

    // Create the ref
    const temporaryMessageRef = useRef<Message | undefined>(undefined);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing) return;
        
        const newWidth = window.innerWidth - e.clientX;
        const clampedWidth = Math.min(Math.max(newWidth, MIN_WIDTH), MAX_WIDTH);
        setWidth(clampedWidth);
    }, [isResizing]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ew-resize';
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    // Keep the ref in sync with state
    useEffect(() => {
        temporaryMessageRef.current = temporaryMessage;
    }, [temporaryMessage]);

    // API function to search users
    const searchUsers = async (query: string, signal?: AbortSignal): Promise<MentionData[]> => {
        try {
            if (!query || query.length < 4) {
                return [];
            }
            const items = await callSearchPatients({
                search: query,
            });

            const users = items.map((item) => {
                return {
                    displayText: item.csn,
                    csn: item.csn,
                    mrn: item.mrn,
                }
            })

            const data = {
                // users: mockUsers,
                users: users,
            }
            return data.users || data; // Adjust based on your API response structure
        } catch (error) {
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw error; // Re-throw abort errors to handle them separately
                }
                throw new Error(`User search failed: ${error.message}`);
            }
            throw new Error('User search failed: Unknown error');
        }
    };

    // Effect to handle debounced API calls
    useEffect(() => {
        const fetchUsers = async () => {
            // Only search if we have a query and mentions are showing
            if (!showMentions || debouncedMentionQuery.length === 0) {
                setUsers([]);
                setIsLoadingUsers(false);
                return;
            }

            // Cancel any existing request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            // Create new abort controller for this request
            abortControllerRef.current = new AbortController();
            
            setIsLoadingUsers(true);
            setUserSearchError("");

            try {
                const searchResults = await searchUsers(debouncedMentionQuery, abortControllerRef.current.signal);
                setUsers(searchResults);
                setUserSearchError("");
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    // Request was cancelled, don't update state
                    return;
                }
                console.error('Error searching users:', error);
                setUsers([]);
                setUserSearchError(error instanceof Error ? error.message : 'Failed to search users');
            } finally {
                setIsLoadingUsers(false);
            }
        };

        fetchUsers();

        // Cleanup function to cancel request on unmount or dependency change
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [debouncedMentionQuery, showMentions]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    useEffect(() => {
        // Close mentions panel when clicking outside
        const handleClickOutside = (event: MouseEvent) => {
            if (mentionPanelRef.current && !mentionPanelRef.current.contains(event.target as Node) &&
                editorRef.current && !editorRef.current.contains(event.target as Node)) {
                setShowMentions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleChatUpdate = (response: Message) => {
        if (response.role === "tool_result") {
            const assistantMetadataS3ID = response.metadata?.output;

            readBinaryFile(`assistant/metadata/${assistantMetadataS3ID}.json`, tenant, `ed-llm-dashboard`).then((data) => {
                const decoder = new TextDecoder();
                const toolOutputArr = JSON.parse(decoder.decode(data));

                if (toolOutputArr && toolOutputArr instanceof Array && toolOutputArr.length > 0) {
                    const toolOutput = toolOutputArr[0].text;
                    if (toolOutput) {
                        const toolOutputObject = JSON.parse(jsonrepair(toolOutput));
                        setScanMulti((scanMulti) => {
                            if (scanMulti) {
                                const scanData = {
                                    ...scanMulti,
                                    ...toolOutputObject,
                                };
                                scanData["citation_mapping"] = generateCitationMapping(scanData);
                                return scanData;
                            } else {
                                let scanData = toolOutputObject;
                                scanData["citation_mapping"] = generateCitationMapping(scanData);
                                return scanData;
                            }
                        });
                    }
                }
            });

            return;
        }

        const currentTemp = temporaryMessageRef.current;

        if (!currentTemp) {
            setTemporaryMessage(response);
            temporaryMessageRef.current = response;
            return;
        }

        if (currentTemp.message_id !== response.message_id) {
            setMessages((orig: Message[]) => [...orig, { ...currentTemp }]);
        }

        setTemporaryMessage(response);
        temporaryMessageRef.current = response;

        if (response.role === "COMPLETED") {
            setIsLoading(false);
            setTemporaryMessage(undefined);
            temporaryMessageRef.current = undefined;
        }
    };

    const loadConversationHistory = async () => {
        setIsLoadingHistory(true);
        try {
            const convos = await callLLMGetConversations(user);
            setConversations(convos);
        } catch (err) {
            console.error("Failed to load conversation history:", err);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const deleteConversation = async (sessionIdToDelete: string, event: React.MouseEvent) => {
        // Stop propagation so clicking delete doesn't also load the conversation
        event.stopPropagation();

        if (!window.confirm('Are you sure you want to delete this conversation?')) {
            return;
        }

        try {
            const success = await callLLMDeleteConversation(sessionIdToDelete);
            if (success) {
                // Remove from the local state
                setConversations(conversations.filter(conv => conv.session_id !== sessionIdToDelete));

                // If the deleted conversation was the current one, start a new chat
                if (sessionId === sessionIdToDelete) {
                    newChat();
                }
            } else {
                alert('Failed to delete conversation');
            }
        } catch (err) {
            console.error("Failed to delete conversation:", err);
            alert('Failed to delete conversation');
        }
    };

    const loadConversation = async (selectedSessionId: string) => {
        // Set the session ID to the selected conversation
        sessionId = selectedSessionId;

        // Close history panel
        setShowHistory(false);

        // Clear current state
        setTemporaryMessage(undefined);
        temporaryMessageRef.current = undefined;

        // Load the conversation messages
        // Note: Backend automatically limits to 100 messages max for performance
        // For extremely long conversations (>100 messages), only the first 100 will be loaded
        // The full conversation context is still preserved in DynamoDB
        setIsLoading(true);
        try {
            const historyMessages = await callLLMGetConversationMessages(selectedSessionId);

            // Show warning if conversation is truncated
            if (historyMessages.length >= 100) {
                console.warn(`Conversation ${selectedSessionId} has ${historyMessages.length}+ messages. Displaying first 100 for performance.`);
            }

            // Convert history messages to the Message format used by the UI
            const loadedMessages: Message[] = historyMessages
                .filter((msg: any) => msg.role === 'user' || msg.role === 'assistant')
                .map((msg: any) => {
                    // Parse metadata if it's a JSON string
                    let metadata = msg.metadata;
                    if (typeof metadata === 'string') {
                        try {
                            metadata = JSON.parse(metadata);
                        } catch (e) {
                            console.error('Failed to parse metadata:', e);
                            metadata = {};
                        }
                    }

                    return {
                        message_id: msg.message_id,
                        role: msg.role,
                        text: msg.text,
                        citations: metadata?.citations || undefined
                    };
                });

            setMessages(loadedMessages);
        } catch (err) {
            console.error("Failed to load conversation messages:", err);
        } finally {
            setIsLoading(false);
        }
    };

    const newChat = () => {
        setIsLoading(false);
        setTemporaryMessage(undefined);
        setShowHistory(false);
        temporaryMessageRef.current = undefined;
        setMessages([]);

        setIsTextDisabled(true);
        setIsWebSocketConnecting(true);
        sessionId = uuidv4();
        socketConnection = getWSService(ws, sessionId, handleWebSocketConnected);
        if (socketConnection.sessionId) {
            // This is because getWSService may actually return a previous open web socket with an older connection ID
            sessionId = socketConnection.sessionId;
        }
        socketConnection.addMessageListener(handleChatUpdate);
    }

    const handleWebSocketConnected = () => {
        setIsTextDisabled(false);
        setIsWebSocketConnecting(false);
    }

    useEffect(() => {
        setIsTextDisabled(true);
        setIsWebSocketConnecting(true);
        sessionId = uuidv4();
        socketConnection = getWSService(ws, sessionId, handleWebSocketConnected);
        if (socketConnection.sessionId) {
            // This is because getWSService may actually return a previous open web socket with an older connection ID
            sessionId = socketConnection.sessionId;
        }
        socketConnection.addMessageListener(handleChatUpdate);
    }, []);

    useEffect(() => {
        scrollCheckpointRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const getTextContent = () => {
        if (!editorRef.current) return '';
        return editorRef.current.textContent || '';
    };

    const getCaretOffset = (editorEl: HTMLElement): number => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return 0;

        const range = selection.getRangeAt(0);

        // Create a range from start of editor to current cursor
        const preCaretRange = document.createRange();
        preCaretRange.setStart(editorEl, 0);
        preCaretRange.setEnd(range.startContainer, range.startOffset);

        return preCaretRange.toString().length;
    };

    const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
        const text = getTextContent();
        if (!editorRef.current) {
            return;
        }
        const cursorOffsetFromBeginning = getCaretOffset(editorRef.current);
        // console.log("handleInput getCaretOffset", cursorOffsetFromBeginning);
        setCursorPosition(cursorOffsetFromBeginning);
        
        if (cursorOffsetFromBeginning > 0) {
            const textBeforeCursor = text.substring(0, cursorOffsetFromBeginning);
            const atMatch = textBeforeCursor.match(/@(\w*)$/);

            if (atMatch) {
                setMentionQuery(atMatch[1]);
                setShowMentions(true);
            } else {
                setShowMentions(false);
                setMentionQuery("");
            }
        }
    };

    function filterRemoveOne<T>(arr: T[], condition: (item: T) => boolean): T[] {
        let removed = false;
        return arr.filter(item => {
            if (condition(item) && !removed) {
            removed = true;
            return false;
            }
            return true;
        });
    }

    useEffect(() => {
        // Add an observe so we know when a tag is deleted
        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                // Check if nodes were removed
                if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
                    mutation.removedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as HTMLElement;
                            if (element.tagName === 'SPAN') {
                                const displayText = element.textContent?.startsWith("@") ? element.textContent.substring(1) : element.textContent;
                                if (mentions.length > 0) {
                                    setMentions(filterRemoveOne(mentions, mention => mention.displayText !== displayText));
                                }
                            }
                        }
                    });
                }
            }
        });

        const editor = editorRef.current;
        if (editor) {
            observer.observe(editor, {
                childList: true,
                subtree: true,
            });
        }

        return () => {
            observer.disconnect();
        };
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
            return;
        }
    };

    const handleAtButtonClick = () => {
        if (editorRef.current) {
            editorRef.current.focus();
            
            const selection = window.getSelection();
            const range = document.createRange();
            
            // Insert @ at current cursor position or at end
            if (selection && selection.rangeCount > 0) {
                const currentRange = selection.getRangeAt(0);
                const textNode = document.createTextNode('@');
                currentRange.insertNode(textNode);
                range.setStartAfter(textNode);
                range.collapse(true);
            } else {
                // Insert at end
                const textNode = document.createTextNode('@');
                editorRef.current.appendChild(textNode);
                range.setStartAfter(textNode);
                range.collapse(true);
            }
            
            selection?.removeAllRanges();
            selection?.addRange(range);
            
            setShowMentions(true);
            setMentionQuery('');
        }
    };

    const handleUserSelect = (user: MentionData) => {
        if (editorRef.current) {

            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);

                let absoluteOffset = 0;

                const walker = document.createTreeWalker(
                    editorRef.current,
                    NodeFilter.SHOW_TEXT,
                    null
                );

                while (walker.nextNode()) {
                    const node = walker.currentNode;
                    absoluteOffset += node.textContent?.length || 0;
                    if (absoluteOffset >= cursorPosition) {
                        break;
                    }
                }

                // Now, compute the position of the last @ before the caret
                let visibleText = "";
                walker.currentNode = editorRef.current;
                while (walker.nextNode()) {
                    const node = walker.currentNode;
                    if (node.nodeType === Node.TEXT_NODE) {
                        visibleText += node.textContent || "";
                    }
                }
                const textBeforeCursor = visibleText.substring(0, absoluteOffset);
                const atIndex = textBeforeCursor.lastIndexOf('@');
                
                if (atIndex !== -1) {
                    // Create mention element
                    const mentionElement = document.createElement('span');
                    mentionElement.className = 'mention-tag';
                    mentionElement.setAttribute('data-mention-id', user.displayText);
                    mentionElement.setAttribute('contenteditable', 'false');
                    mentionElement.textContent = `@${user.displayText}`;
                    mentionElement.style.backgroundColor = '#e3f2fd';
                    mentionElement.style.color = '#1976d2';
                    mentionElement.style.padding = '2px 6px';
                    mentionElement.style.borderRadius = '12px';
                    mentionElement.style.fontSize = '14px';
                    mentionElement.style.fontWeight = '500';
                    mentionElement.style.margin = '0 2px';
                    mentionElement.style.display = 'inline-block';
                    mentionElement.style.cursor = 'default';
                    
                    // Find the @ symbol and replace it with the mention
                    const walker = document.createTreeWalker(
                        editorRef.current,
                        NodeFilter.SHOW_TEXT,
                        null
                    );
                    
                    let currentPos = 0;
                    let node = walker.nextNode();
                    
                    while (node) {
                        const nodeText = node.textContent || '';
                        const nodeLength = nodeText.length;
                        
                        if (currentPos <= atIndex && atIndex < currentPos + nodeLength) {
                            const localAtIndex = atIndex - currentPos;
                            const beforeAt = nodeText.substring(0, localAtIndex);
                            const afterQuery = nodeText.substring(localAtIndex + 1 + mentionQuery.length);
                            
                            // Split the text node
                            const parentNode = node.parentNode;
                            if (parentNode) {
                                // Create text nodes for before and after
                                if (beforeAt) {
                                    const beforeNode = document.createTextNode(beforeAt);
                                    parentNode.insertBefore(beforeNode, node);
                                }
                                
                                // Insert mention
                                parentNode.insertBefore(mentionElement, node);
                                
                                // Add space after mention
                                const spaceNode = document.createTextNode(' ');
                                parentNode.insertBefore(spaceNode, node);
                                
                                // Update the original node with remaining text
                                if (afterQuery) {
                                    node.textContent = afterQuery;
                                } else {
                                    parentNode.removeChild(node);
                                }
                                
                                // Position cursor after the mention
                                const newRange = document.createRange();
                                newRange.setStartAfter(spaceNode);
                                newRange.collapse(true);
                                selection.removeAllRanges();
                                selection.addRange(newRange);
                            }
                            break;
                        }
                        
                        currentPos += nodeLength;
                        node = walker.nextNode();
                    }

                    // Add to mentions list
                    setMentions(prev => [...prev, user]);
                }
            }
            
            setShowMentions(false);
            setMentionQuery("");
            editorRef.current.focus();
        }
    };

    const handleSend = () => {
        if (isLoading) {
            return;
        }

        const text = getTextContent();
        if (text.trim()) {
            sendPrompt(text);
        }
    };

    const sendPrompt = (text: string) => {
        console.log('Sending message:', text);
        console.log('Mentions:', mentions);

        const messageId = uuidv4();
        setMessages((origConvos: Message[]) => {
            return [...origConvos, {
                message_id: messageId,
                role: "user",
                text: text
            }]
        });

        // Ping chat to try to mitigate cold start
        callLLMPing().then(() => {
            console.log("Chat pinged...");
        });

        setIsLoading(true);

        const pageContext = getPageContext();
        const viewing = [];
        if (pageContext && pageContext["patient_visit"]) {
            viewing.push({
                csn: pageContext["patient_visit"].csn,
                mrn: pageContext["patient_visit"].mrn,
            });
        }
        const additionalMetadata: any = {
            "mentioned": mentions,
            "viewing": viewing,
        };

        callLLMChat(messageId, sessionId, user, text, messages, additionalMetadata).then((response) => {
            console.log("Chat submitted...");

            trackEvent('chat_submit', user?.username, { text: truncateString(text) });
        }).catch((err: any) => {
            console.error(err);
            setMessages((origConvos: any) => {
                return [...origConvos, {
                    message_id: messageId,
                    user: "assistant",
                    text: "The assistant ran into an error and could not answer"
                }]
            });
        });

        // Clear editor
        if (editorRef.current) {
            editorRef.current.innerHTML = '';
        }
        setMentions([]);
    };

    const renderMentionPanel = () => {
        if (isLoadingUsers) {
            return (
                <div style={{ padding: '16px 12px', textAlign: 'center', color: '#6c757d' }}>
                    <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
                    Searching users...
                </div>
            );
        }

        if (userSearchError) {
            return (
                <div style={{ padding: '8px 12px', color: '#dc3545', fontSize: '14px' }}>
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {userSearchError}
                </div>
            );
        }

        if (users.length === 0 && debouncedMentionQuery.length > 0) {
            return (
                <div style={{ padding: '8px 12px', color: '#6c757d' }}>
                    No users found for "{debouncedMentionQuery}"
                </div>
            );
        }

        if (users.length === 0) {
            return (
                <div style={{ padding: '8px 12px', color: '#6c757d' }}>
                    Start typing to search users...
                </div>
            );
        }

        return users.map(user => (
            <div
                key={user.displayText}
                className="mention-item"
                onClick={() => handleUserSelect(user)}
                style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
            >
                <div>
                    <div style={{ fontWeight: '500', fontSize: '14px' }}>{user.csn}</div>
                </div>
            </div>
        ));
    };

    const renderSingleMessage = (message: Message, isRunning: boolean) => {
        if (message.role === "user") {
            return (
                <div key={message.message_id} className="assistant-chat-human">
                    {message.text && <MarkdownRenderer content={message.text} />}
                </div>
            );
        } else if (message.role === "tool") {
            return (
                <div key={message.message_id} className="assistant-chat-tool">
                    <ToolMetadataBox
                        toolName={message.metadata && message.metadata.name ? message.metadata.name : "Unknown Tool"}
                        metadata={message.metadata}
                        isRunning={isRunning}
                    />
                </div>
            );
        } else if (message.role === "assistant") {
            return (
                <>
                    <div key={message.message_id} className="assistant-chat-bot mb-2">
                        {message.text && <MarkdownRenderer content={message.text} citations={message.citations} scanMulti={scanMulti} />}
                        {/* {JSON.stringify(message)} */}
                    </div>
                    {/* <UpdateDashboardButton message={message} messages={messages} /> */}
                    {
                        !isLoading && (
                            <div className="assistant-chat-feedback-bar">
                                <CopyToClipboardButton user={user} module={"chat"} onClick={() => {navigator.clipboard.writeText(message.text ?? "")}} />
                                <FeedbackModal isPositiveFeedback={true} messageId={message.message_id} activeFeedback={messageIdToFeedback} setActiveFeedback={setMessageIdToFeedback} />
                                <FeedbackModal isPositiveFeedback={false} messageId={message.message_id} activeFeedback={messageIdToFeedback} setActiveFeedback={setMessageIdToFeedback} />
                            </div>
                        )
                    }
                </>
            );
        } else {
            return <></>;
        }
    }



    return (
        <div ref={panelRef} className={`assistant-panel ${isOpen ? 'open' : ''}`} style={{ width: `${width}px` }}>
            <div
                className="resize-handle"
                onMouseDown={handleMouseDown}
            />
            <div className="assistant-header">
                <Row>
                    <Col>
                        Capacity <strong>Assistant</strong>
                    </Col>
                    <Col className='text-right'>
                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Conversation History
                                </Tooltip>
                            }
                        >
                            <span className="assistant-close-btn me-3" onClick={() => {
                                setShowHistory(!showHistory);
                                if (!showHistory) {
                                    loadConversationHistory();
                                }
                            }}>
                                <FontAwesomeIcon icon={faHistory} />
                            </span>
                        </OverlayTrigger>

                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    New Conversation
                                </Tooltip>
                            }
                        >
                            <span className="assistant-close-btn me-3" onClick={newChat}><FontAwesomeIcon icon={faPenToSquare} /></span>
                        </OverlayTrigger>

                        <OverlayTrigger
                            placement="bottom"
                            overlay={
                                <Tooltip>
                                    Close Assistant
                                </Tooltip>
                            }
                        >
                            <span className="assistant-close-btn" onClick={() => setIsOpen(false)}><FontAwesomeIcon icon={faClose} /></span>
                        </OverlayTrigger>
                    </Col>
                </Row>
            </div>

            {/* Conversation History Panel */}
            {showHistory && (
                <div className="assistant-history-panel" style={{
                    position: 'absolute',
                    top: '60px',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #dee2e6',
                    overflowY: 'auto',
                    padding: '16px',
                    zIndex: 1000
                }}>
                    <div className="mb-3"><strong>Conversation History</strong></div>
                    {isLoadingHistory ? (
                        <div className="text-center mt-4">
                            <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                        </div>
                    ) : conversations.length === 0 ? (
                        <div className="text-center mt-4 text-muted">
                            No previous conversations found
                        </div>
                    ) : (
                        <div>
                            {conversations.map((conv) => (
                                <div
                                    key={conv.session_id}
                                    className="assistant-history-item"
                                    onClick={() => loadConversation(conv.session_id)}
                                    style={{
                                        padding: '12px',
                                        marginBottom: '8px',
                                        backgroundColor: 'white',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        border: '1px solid #dee2e6',
                                        transition: 'all 0.2s',
                                        position: 'relative'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#e9ecef';
                                        e.currentTarget.style.borderColor = '#adb5bd';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'white';
                                        e.currentTarget.style.borderColor = '#dee2e6';
                                    }}
                                >
                                    <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', paddingRight: '30px' }}>
                                        {conv.first_message || 'New conversation'}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                        {getUserFriendlyDateStringWithoutSecond(new Date(conv.created_at))} • {conv.message_count} messages
                                    </div>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={(e) => deleteConversation(conv.session_id, e)}
                                        style={{
                                            position: 'absolute',
                                            top: '8px',
                                            right: '8px',
                                            padding: '4px 8px',
                                            textDecoration: 'none'
                                        }}
                                        className="delete-conversation-btn"
                                    >
                                        <FontAwesomeIcon icon={faTrash} size="sm" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="assistant-body">
                {
                    isWebSocketConnecting ? (
                        <>
                            <div style={{marginTop: '50%', marginLeft: 'auto', marginRight: 'auto', display: 'table'}}>
                                <FontAwesomeIcon icon={faSpinner} spin /><span style={{marginLeft: '12px'}}>Loading...</span>
                            </div>
                        </>
                    ) : messages.length === 0 ? (
                        <div className="mt-4">
                            <h3 className="mt-4 gradient-text">Hello! I'm your clinical copilot.</h3>

                            <p className="mt-4 mb-4">Capacity uses generative AI to answer questions over the patient data. Try one of the sample prompts below:</p>

                            <div className="assistant-box assistant-box-prompt" onClick={() => {sendPrompt("What kind of questions can I ask?")}}>
                                What kind of questions can I ask?
                            </div>
                            <div className="assistant-box assistant-box-prompt" onClick={() => {sendPrompt("How old is this patient?")}}>
                                How old is this patient?
                            </div>
                            <div className="assistant-box assistant-box-prompt" onClick={() => {sendPrompt("Has the patient been given any rate control medications?")}}>
                                Has the patient been given any rate control medications?
                            </div>
                        </div>
                    ) : (                       
                        <>
                            {
                                messages.map((message) => {
                                    return renderSingleMessage(message, false);
                                })
                            }
                        </>
                    )
                }
                {
                    temporaryMessage && renderSingleMessage(temporaryMessage, true)
                }
                {
                    isLoading && (
                        <div className='mt-2'>
                            <FontAwesomeIcon icon={faCircle} className="pulsating-dot" />
                        </div>
                    )
                }
                {
                    debug && (
                        <>
                            <div>
                                Page Context: {JSON.stringify(getPageContext())}
                            </div>
                        </>
                    )
                }
                <div ref={scrollCheckpointRef} />
            </div>
            <Container className="assistant-bottom">
                <Row className="assistant-bottom-text-area-container">
                    <Col className="position-relative">
                        {/* Mention Panel */}
                        {showMentions && (
                            <div 
                                ref={mentionPanelRef}
                                className="mention-panel"
                                style={{
                                    position: 'absolute',
                                    bottom: '100%',
                                    left: 0,
                                    right: 0,
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '8px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    zIndex: 1000,
                                    boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
                                }}
                            >
                                {renderMentionPanel()}
                            </div>
                        )}
                        
                        {/* Contenteditable Editor */}
                        {getTextContent() === '' && (
                            <div className="assistant-placeholder-overlay">
                                Ask Capacity a question...
                            </div>
                        )}
                        <div
                            ref={editorRef}
                            contentEditable={!isTextDisabled}
                            onInput={handleInput}
                            onKeyDown={handleKeyDown}
                            className="assistant-editor"
                            style={{
                                width: '100%',
                                minHeight: '40px',
                                maxHeight: '120px',
                                border: 'none',
                                outline: 'none',
                                padding: '10px 50px 10px 10px',
                                fontFamily: 'inherit',
                                fontSize: '14px',
                                lineHeight: '1.4',
                                backgroundColor: 'transparent',
                                overflowY: 'auto',
                                wordWrap: 'break-word',
                                whiteSpace: 'pre-wrap'
                            }}
                        />
                        
                        {/* Actions Bar */}
                        <div 
                            className="assistant-actions"
                            style={{
                                position: 'absolute',
                                bottom: '8px',
                                right: '8px',
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center'
                            }}
                        >
                            <Button
                                variant="link"
                                size="sm"
                                onClick={handleAtButtonClick}
                                style={{
                                    padding: '4px 8px',
                                    color: '#6c757d',
                                    textDecoration: 'none'
                                }}
                                className="at-button"
                            >
                                <FontAwesomeIcon icon={faAt} />
                            </Button>
                            
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleSend}
                                disabled={!getTextContent().trim()}
                                style={{
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                className="send-button"
                            >
                                <FontAwesomeIcon icon={faPaperPlane} size="sm" />
                            </Button>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default AssistantPanel;
